<template>
  <div class="date-selector">
    <el-date-picker
      ref="date_selector"
      class="inline-date-select"
      v-bind:value="selectedDate"
      :clearable="!defaultIsToday"
      :type="type"
      :placeholder="placeholder"
      :size="size"
      :disabled="disabled"
      :picker-options="Object.assign(datePickerOptions, {disabledDate: disabledDateCallback})"
      @input="onInput"
      @change="onUpdate"
      @focus="$emit('focus', $event)"
      @blur="onUnfocus">
    </el-date-picker>
    <el-tooltip
      v-if="showTimezoneTooltip"
      :content="`You're timezone is: ${userTimezone}`"
      placement="top">
     <i class="el-icon-info" style="cursor: help"></i>
   </el-tooltip>
  </div>
</template>

<script>
import moment from '@/moment-gp.js';

// TODO: today is sometimes disabled (timezone issue?)

function getDateWithOffsetDays(offset_days) {
  const date = moment.currentDate(); // new Date();
  date.add( offset_days, 'days' );
  // date.setTime( date.getTime() + 3600 * 1000 * 24 * offset_days );
  // date.setHours(0, 0, 0, 0);
  return date;
}

const defaultShortcuts = [{
  text: 'Today',
  onClick(picker) {
    picker.$emit('pick', getDateWithOffsetDays(0));
  }
},
{
  text: 'Tomorrow',
  onClick(picker) {
    picker.$emit('pick', getDateWithOffsetDays(1));
  }
},
{
  text: '+1 week',
  onClick(picker) {
    picker.$emit('pick', getDateWithOffsetDays(7));
  }
},
{
  text: '+1 month',
  onClick(picker) {
    picker.$emit('pick', getDateWithOffsetDays(30)); // or 31?
  }
},
{
  text: 'Unset',
  onClick(picker) {
    picker.$emit('pick', undefined);
  }
}]

export default {
  props: {
    type: {
      default: "date"
    },
    placeholder: {
      default: 'Pick a day'
    },
    size: {
      default: 'medium'
    },
    disabled: {
      default: false
    },
    showTimezoneTooltip: {
      default: true // TODO: This will we turned off soon, but not it helps to identify el-date-pickers
    },
    value: {
      /*
        Elemen UI DatePicker, will accept anything that can be passed to new Date(),
        which is:

        new Date(value);
        new Date(dateString);
        new Date(year, monthIndex [, day [, hours [, minutes [, seconds [, milliseconds]]]]]);

        value: Integer value representing the number of milliseconds since
        January 1, 1970, 00:00:00 UTC, with leap seconds ignored (Unix Epoch; but
        consider that most Unix timestamp functions count in seconds).

        Note: parsing of date strings with the Date constructor (and Date.parse,
        they are equivalent) is strongly discouraged due to browser differences and inconsistencies.
        https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date
      */
      default: null
    },
    disableBefore: {
      default: null // '2018-11-20'
    },
    defaultIsToday: {
      default: false
    },
    userTimezoneOverwrite: {
      default: null
    },
  },
  data: () => ({
    selectedDate: null,
    datePickerOptions: {
      shortcuts: defaultShortcuts
    },
    beforeDate: null,
    userTimezone: null
  }),
  created() {
    if ( this.disableBefore ) {
      /*
      1st: check for special string 'today'
      2nd: check if Firestore Timestamp
      3rd: check for regular date
      */
      if ( this.disableBefore === 'today' ) {
        // let startOfDay = new Date();
        // startOfDay.setHours(0,0,0,0);
        // this.beforeDate = startOfDay;
        this.beforeDate = moment.currentDate();
      } else if ( typeof this.disableBefore === 'object' &&
        this.disableBefore.constructor.name === 'Timestamp') { // TODO: might not work in production
        this.beforeDate = this.disableBefore.toDate();
      } else {
        this.beforeDate = moment(this.disableBefore).toDate();
      }
      /* Convert to numeric time */
      this.beforeDate = this.beforeDate.getTime();
    }

    /* Allow to override the userTimezone. If not set user global user timezone */
    this.userTimezone = this.userTimezoneOverwrite || this.user_get_timezone;
  },
  watch: {
    value: {
      handler: function (value) {

        // TODO move to store/util/mixin?

        /* Make sure we have something moment() can read */
        if ( !value ) {
          /* check for null */
          this.selectedDate = null;
        } else if ( moment.isMoment( value ) ) {
          /* check if moment() */
          this.selectedDate = value.isValid() ? value : null;
          /* The moment Object might have a timezone but we ignore it here
             Otherwise the offset would be value.utcOffset() */
        } else if ( moment.isDate( value ) ) {
          /* check if Date() */
          this.selectedDate = value;
          /* The date might have a timeoffset but we ignore it
             Otherwise the offset would be value.getTimezoneOffset() */
        } else if ( typeof value === 'object' && value.constructor.name === 'Timestamp') { // TODO: might not work in production
          /* check if Firestore Timestamp and convert to Date */
          // TODO: also check if we got an object with { seconds, milliseconds }
          this.selectedDate = value.toDate();
        } else if ( !isNaN( value ) ) {
          /* check for timestamp */
          this.selectedDate = value;
        } else {
          this.selectedDate = null;
          console.warn("DateSelector got invalid value", value);
        }

        if ( this.selectedDate === null ) {
          /* There is a bug with el-datepicker where nothing can be selected
             when the value is set to null. Setting the value to undefined
             apparently solves the problem. */
          this.selectedDate = undefined;
          return; /* We're done */
        }

        if ( this.userTimezone ) {
          /* The user has selected a timezone that might be different from the
             browser timezone. So we need to use a 'fake date' for element ui */
          let momentObj = moment( value );
          this.selectedDate = momentObj.toDatepicker();
          // DELME: convert_localMomentObj_to_usertime( momentObj, this.userTimezone );

          // console.log("Updated input from", momentObj.format(), "to timezone", this.userTimezone, ":", this.selectedDate.format());
        } else {
          this.selectedDate = moment( value );
        }
      },
      immediate: true
    }
  },
  computed: {
    user_get_timezone() {
      return this.$models.userSettings.getters.user_get_timezone
    }
  },
  methods: {
    disabledDateCallback(time) {
      return this.disableBefore ? time.getTime() < this.beforeDate : false;
    },

    onUpdate() {
      if ( this.selectedDate ) {
        let localMomentObj = moment.fromDatepicker( this.selectedDate );
        // DELME: let localMomentObj = convert_usertime_to_localMomentObj( this.selectedDate, this.userTimezone );
        this.$emit('input', localMomentObj);

        // console.log("onUpdate: converting from ", this.selectedDate, "to", localMomentObj ? localMomentObj.format() : 'N/A');
      } else {
        this.$emit('input', null)
        // console.log("Date cleared, emitted null");
      }
      this.$emit('change') // Required by DateSelectorInline
    },

    onInput($event) {
      this.selectedDate = $event
    },

    onUnfocus() {
      this.$emit('blur')
    },

    focus() {
      this.$refs.date_selector.focus();
    }
  }
}
</script>

<style>
.date-selector {
  display: inline-flex;
  align-items: center;
}
</style>
